import { makeAsyncCardComponent } from '../internal/makeAsyncCardComponent';
import CardContentErrorBoundary from '../components/CardContentErrorBoundary';
import CardContentLoadingComponent from '../components/skeletons/CardContentLoadingComponent';
import FormattedCard from '../components/FormattedCard';
import { CARD_TYPES } from '../constants/CardType';
import { useCardLocationContext } from '../internal/CardLocationContext';
import { withCardActionsResolver } from './withCardActionsResolver';
import { withCardTitleResolver } from './withCardTitleResolver';
import { withContextProvider } from './withContextProvider';
import { useCardHookContext } from '../internal/useCardHookContext';
import { CONTENT_SELECTOR } from '../constants/CardSeleniumSelectors';
import CardPerfHookWarningBanner from '../internal/perf/CardPerfHookWarningBanner';
import { LEGACY_CARDS_METADATA_MAP } from '../constants/LegacySidebarCardMetadata';
import PerformanceMarker from 'crm-fe-perf/timing/PerformanceMarker';
import { getRealCardContentStart } from 'crm-fe-perf/timing/marks/crm-cards-sdk/performanceMarks';
import { isLegacyCard } from '../utils/isLegacyCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CARD_TYPES_ENABLED_FOR_LEGACY_ACTIONS = [...Object.keys(LEGACY_CARDS_METADATA_MAP).filter(cardType => LEGACY_CARDS_METADATA_MAP[cardType].renderAsLegacyCardInCrmLocationResolver), CARD_TYPES.LEGACY_SIDEBAR_CARD, CARD_TYPES.ASSOCIATION_V3, CARD_TYPES.DOCUSIGN_V2, CARD_TYPES.ATTACHMENTS];
const DEFAULT_LEGACY_DATA_ATTRIBUTES_HOOK = () => ({});
function getContentComponent({
  contentComponentLoader,
  ContentComponent,
  LoadingComponent
}) {
  if (!contentComponentLoader && !ContentComponent) {
    throw new Error('You must provide either a contentComponentLoader or a ContentComponent');
  }
  if (contentComponentLoader && ContentComponent) {
    throw new Error('You must provide either a contentComponentLoader or a ContentComponent, not both');
  }
  if (ContentComponent) {
    return ContentComponent;
  }
  return makeAsyncCardComponent({
    loader: contentComponentLoader,
    LoadingComponent
  });
}

/**
 * @deprecated using `contentComponentLoader` and `titleComponentLoader` props is deprecated. Please use `ContentComponent` and `TitleComponent` props instead.
 */

export function buildCrmCard(args) {
  const {
    displayName,
    enableLegacyActionFormat = false,
    LoadingComponent = CardContentLoadingComponent,
    useCardProps,
    useCardActions,
    context,
    useContextValue,
    useLegacyDataAttributes = DEFAULT_LEGACY_DATA_ATTRIBUTES_HOOK
  } = args;
  const CardContentComponent = getContentComponent({
    contentComponentLoader: 'contentComponentLoader' in args ? args.contentComponentLoader : undefined,
    ContentComponent: 'ContentComponent' in args ? args.ContentComponent : undefined,
    LoadingComponent
  });
  const CrmCardRenderer = props => {
    const cardLocationContext = useCardLocationContext();
    const cardContext = useCardHookContext();
    const legacyDataAttributes = useLegacyDataAttributes(props, cardContext);
    return /*#__PURE__*/_jsx(FormattedCard, {
      cardData: props.cardData,
      actionConfigs: props.actionConfigs,
      actionComponent: props.actionComponent,
      hasActionsLoadingError: props.hasActionsLoadingError,
      title: props.cardTitle,
      CardFormatter: props.CardFormatter,
      legacyDataAttributes: legacyDataAttributes,
      ActionFormatter: props.ActionFormatter,
      usageTracker: props.usageTracker,
      children: /*#__PURE__*/_jsx(CardContentErrorBoundary, {
        cardData: props.cardData,
        location: cardLocationContext.location,
        loggingTag: cardLocationContext.loggingTag,
        children: /*#__PURE__*/_jsxs("div", {
          "data-test-id": CONTENT_SELECTOR,
          "data-selenium-test": `card-id-${props.cardData.definition.cardId}`,
          children: [/*#__PURE__*/_jsx(CardPerfHookWarningBanner, {}), /*#__PURE__*/_jsx(CardContentComponent, Object.assign({}, props.cardProps))]
        })
      })
    });
  };
  CrmCardRenderer.displayName = `CrmCardRenderer(${displayName})`;
  const CardWithTitleResolver = withCardTitleResolver({
    titleComponentLoader: 'titleComponentLoader' in args ? args.titleComponentLoader : undefined,
    TitleComponent: 'TitleComponent' in args ? args.TitleComponent : undefined,
    titleOptions: 'titleOptions' in args ? args.titleOptions : undefined
  })(CrmCardRenderer);
  const CardWithTitleAndActionResolver = withCardActionsResolver({
    useCardActions,
    enableLegacyActionFormat,
    cardTypesEnabledForLegacyActions: CARD_TYPES_ENABLED_FOR_LEGACY_ACTIONS
  })(CardWithTitleResolver);
  const CardWithTitleActionAndContextResolvers = withContextProvider(context, useContextValue)(CardWithTitleAndActionResolver);
  const CrmCard = props => {
    // The content component, title, and actions all need these mapped props, so
    // call the hook here and pass the props down.
    // Any errors thrown here will be caught in crm-cards/internal/CardErrorBoundary
    const cardContext = useCardHookContext();
    const cardProps = useCardProps(props, cardContext);
    const exactCardType = isLegacyCard(props.cardData) ? props.cardData.configuration.cardType : props.cardData.definition.cardType;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(PerformanceMarker, {
        marker: getRealCardContentStart(exactCardType, props.cardData.definition.cardId),
        skipMetrics: true
      }), /*#__PURE__*/_jsx(CardWithTitleActionAndContextResolvers, Object.assign({}, props, {
        cardProps: cardProps
      }))]
    });
  };
  CrmCard.displayName = displayName;
  return CrmCard;
}